import services from "@/composable/services";
import useArray from "@/composable/components/array";
import useNumber from "@/composable/components/number";
import useBoolean from "@/composable/components/boolean";
import { ApplicationLightResult } from "@/types/generated/UsedGeneratedTypes";
import { LocalApplicationLightResult } from "@/types/front/LocalApplicationLightResult";

type ReactiveProgressEvent = {
  type: "REACTIVE_PROGRESS";
  result: number;
};

type ReactiveResultEvent = {
  type: "REACTIVE_RESULT";
  result: ApplicationLightResult;
};

type AddApplicationEvent = ReactiveProgressEvent | ReactiveResultEvent;

const { shallowRefArray: applications } = useArray<LocalApplicationLightResult>();
const { shallowRefArray: ownApplications } = useArray<LocalApplicationLightResult>();
const { refBoolean: hasOwnApplications } = useBoolean();
const { refNumber: showOwnApplications } = useNumber();
const { shallowRefArray: otherApplications } = useArray<LocalApplicationLightResult>();
const { refNumber: progress } = useNumber(0);

function addApplication(result: AddApplicationEvent): void {
  if (result.type === "REACTIVE_PROGRESS") {
    progress.value = result.result;
  }
  else if (result.type === "REACTIVE_RESULT") {
    const applicationLightResult = result.result;
    const authenticatedUser = services.loginService.getAuthenticatedUser(applicationLightResult.application.name);
    services.loginService.invalidateCharte(applicationLightResult.application.id, authenticatedUser);
    const localApplicationLightResult = services.internationalisationService.mergeInternationalizationApplicationLightResult(applicationLightResult);
    // Recherche de l'application dans la liste, sinon ajout
    const id = applications.value
      .map((a, i) => (a.application.name === localApplicationLightResult.application.name ? i : -1))
      .find(i => i >= 0);
    const localApplication = [...applications.value];
    if (id !== undefined && id >= 0) {
      localApplication[id] = localApplicationLightResult;
    }
    else {
      localApplication.push(localApplicationLightResult);
    }
    ownApplications.value = localApplication.filter(app => services.applicationService.isApplicationUser(app));
    otherApplications.value = localApplication.filter(app => !services.applicationService.isApplicationUser(app));
    hasOwnApplications.value = ownApplications.value.length > 0;
    showOwnApplications.value = hasOwnApplications.value ? 0 : 1;
    applications.value = localApplication;
  }
}

async function loadApplications(filter: string[]): Promise<void> {
  // Le Fetcher transmet le JSON parsé (AddApplicationEvent) au callback via NDJSON streaming.
  // Le type déclaré '(message: string) => void' dans getApplications est inexact par rapport
  // au contrat réel — cast explicite pour documenter ce décalage sans bloquer TS.
  await services.applicationService.getApplications(
    addApplication as unknown as (message: string) => void,
    filter,
  );
}

function useApplication() {
  return {
    progress,
    applications,
    ownApplications,
    otherApplications,
    loadApplications,
    hasOwnApplications,
    showOwnApplications,
  };
}

export default useApplication;
